import type { ReactNode } from 'react'
import styled from 'styled-components'

const Card = styled.div<{ $fullWidth: boolean }>`
  background: #ffffff;
  border-radius: 16px;
  padding: 16px 24px;
  /* Edit cards space themselves via this margin; view cards (fullWidth) are
     spaced by their parent's gap, so they drop it to avoid a double gap. */
  margin-bottom: ${({ $fullWidth }) => ($fullWidth ? '0' : '24px')};
`

const Title = styled.h2<{ $flush: boolean }>`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  margin: 0 0 ${({ $flush }) => ($flush ? '0' : '16px')};
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TitleDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin: 0 0 16px;
`

// Segmented radio groups (Status, Allergies, GP connect, …) render as
// equal-width pills filling the field column, matching Figma. Spread onto a
// `<Field type="radios">`.
export const segmentedRadio = {
  style: { gap: 16 },
  optionFieldStyle: { flex: '1 1 0', minWidth: 0, marginRight: 0 }
}

// Fields sit in a constrained, left-aligned column (label + input ~720px) so
// inputs don't stretch the full card width — matches the Figma edit layout.
const Body = styled.div`
  max-width: 752px;
`

// Uniform vertical spacing between field rows within a card. Use this to wrap
// a card's fields so every row is evenly spaced (legacy fields each carried
// their own ad-hoc margins, which read as inconsistent gaps).
export const CardFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

type EditProfileCardProps = {
  title: string
  children: ReactNode
  testId?: string
  // View cards (CM-3664) render a divider directly under the title; edit
  // cards leave it off (default).
  showTitleDivider?: boolean
  // View cards (CM-3664) need their content to span the full card width
  // (e.g. the header metadata row, the assignment list). Edit cards keep the
  // 752px Body constraint for form inputs (default).
  fullWidth?: boolean
  // Drop the title's bottom margin so supporting text sits flush beneath it
  // (CM-3814). Off by default to preserve the standard title spacing.
  flushTitle?: boolean
}

const EditProfileCard = ({
  title,
  children,
  testId,
  showTitleDivider = false,
  fullWidth = false,
  flushTitle = false
}: EditProfileCardProps) => (
  <Card className="lmc-card" data-testid={testId} $fullWidth={fullWidth}>
    {title && <Title $flush={flushTitle}>{title}</Title>}
    {showTitleDivider && <TitleDivider />}
    {fullWidth ? children : <Body>{children}</Body>}
  </Card>
)

export default EditProfileCard
